import { useCallback, useEffect, useMemo, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate } from "@/lib/format";
import type { components } from "@dolfin/api-admin";
import { NewSupplierCreditNoteDialog } from "@/components/NewSupplierCreditNoteDialog";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileMinus, Plus } from "lucide-react";
import {
  EmptyState,
  ErrorState,
  Eyebrow,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import { cn } from "@/lib/utils";

type SupplierCreditNoteResponse = components["schemas"]["SupplierCreditNoteResponse"];
type SupplierResponse = components["schemas"]["SupplierResponse"];
type SupplierCreditBalanceResponse = components["schemas"]["SupplierCreditBalanceResponse"];
type SupplierCreditNoteState = components["schemas"]["SupplierCreditNoteState"];

const PAGE_SIZE = 10;
const ALL_SUPPLIERS = "__all__";
const ALL_STATUSES = "__all__";
const STATUSES: SupplierCreditNoteState[] = ["Draft", "Issued", "PartiallyApplied", "Applied"];

function statusLabel(status: string | undefined): string {
  return status === "PartiallyApplied" ? "Partially applied" : (status ?? "-");
}

function StatusPill({ status }: { status: string | undefined }) {
  const available = status === "Issued" || status === "PartiallyApplied";
  return (
    <Badge
      variant="outline"
      className={cn(
        available
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          : "border-border bg-muted text-muted-foreground",
      )}
    >
      {statusLabel(status)}
    </Badge>
  );
}

export interface SupplierCreditNotesListPageProps {
  defaultSupplierId?: string;
  onSupplierFilterChange?: (supplierId: string | undefined) => void;
  onSupplierClick?: (supplierId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
}

export function SupplierCreditNotesListPage({
  defaultSupplierId,
  onSupplierFilterChange,
  onSupplierClick,
  onCreditNoteClick,
}: SupplierCreditNotesListPageProps) {
  const api = useDolfinClient();

  const [notes, setNotes] = useState<SupplierCreditNoteResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [supplierId, setSupplierId] = useState<string>(defaultSupplierId ?? ALL_SUPPLIERS);
  const [status, setStatus] = useState<string>(ALL_STATUSES);

  const [balances, setBalances] = useState<SupplierCreditBalanceResponse[]>([]);

  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [newOpen, setNewOpen] = useState(false);

  const supplierName = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of suppliers) if (s.id) map.set(s.id, s.name);
    return map;
  }, [suppliers]);

  useEffect(() => {
    setSupplierId(defaultSupplierId ?? ALL_SUPPLIERS);
  }, [defaultSupplierId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.GET("/v1/suppliers", { params: { query: { Limit: 100 } } });
      if (!cancelled) setSuppliers(data?.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const fetchNotes = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = { Limit: PAGE_SIZE };
        if (supplierId !== ALL_SUPPLIERS) query.SupplierId = supplierId;
        if (status !== ALL_STATUSES) query.Status = status;
        if (cursor) query.Cursor = cursor;

        const { data, error: apiError } = await api.GET("/v1/supplier-credit-notes", {
          params: { query },
        });
        if (apiError || !data) {
          setError(apiErrorMessage(apiError, "Failed to load supplier credit notes"));
          return;
        }
        setNotes(data.data ?? []);
        setNextCursor(data.meta?.cursors?.next ?? null);
      } finally {
        setLoading(false);
      }
    },
    [api, supplierId, status],
  );

  const fetchBalances = useCallback(async () => {
    if (supplierId === ALL_SUPPLIERS) {
      setBalances([]);
      return;
    }
    const { data } = await api.GET("/v1/suppliers/{supplierId}/credit", {
      params: { path: { supplierId } },
    });
    setBalances(data ?? []);
  }, [api, supplierId]);

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchNotes();
    fetchBalances();
  }, [fetchNotes, fetchBalances]);

  function refresh() {
    fetchNotes(cursorStack[cursorStack.length - 1]);
    fetchBalances();
  }

  function handleSupplierChange(value: string) {
    setSupplierId(value);
    onSupplierFilterChange?.(value === ALL_SUPPLIERS ? undefined : value);
  }

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchNotes(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    setCursorStack(newStack);
    fetchNotes(newStack[newStack.length - 1]);
  }

  const currentPage = cursorStack.length + 1;

  const supplierFiltered = supplierId !== ALL_SUPPLIERS;
  const newButton = (
    <Button onClick={() => setNewOpen(true)}>
      <Plus className="size-4" />
      New credit note
    </Button>
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Supplier credit notes"
        subtitle="Credit your suppliers owe you - open one to issue it and net it against bills you owe them."
        actions={newButton}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value={ALL_STATUSES}>All</TabsTrigger>
            {STATUSES.map((s) => (
              <TabsTrigger key={s} value={s}>
                {statusLabel(s)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select value={supplierId} onValueChange={handleSupplierChange}>
          <SelectTrigger className="h-9 w-full sm:w-56" aria-label="Filter by supplier">
            <SelectValue placeholder="All suppliers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SUPPLIERS}>All suppliers</SelectItem>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.id!}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {balances.some((b) => Number(b.available ?? 0) !== 0) && (
        <div className="mt-4 flex flex-wrap gap-3">
          {balances
            .filter((b) => Number(b.available ?? 0) !== 0)
            .map((b) => (
              <div key={b.currency} className="rounded-xl border bg-card/60 px-4 py-3 backdrop-blur-sm">
                <Eyebrow>Available from {supplierName.get(supplierId) ?? "this supplier"}</Eyebrow>
                <p className="mt-0.5 text-xl font-bold tracking-tight text-emerald-700 tabular-nums dark:text-emerald-400">
                  {formatCurrency(b.available, b.currency)}
                </p>
              </div>
            ))}
        </div>
      )}

      <div className="pt-4">
        {error ? (
          <ErrorState message={error} onRetry={() => fetchNotes(cursorStack[cursorStack.length - 1])} />
        ) : loading ? (
          <LoadingState label="Loading credit notes..." />
        ) : notes.length === 0 ? (
          <EmptyState
            icon={FileMinus}
            title={
              status !== ALL_STATUSES
                ? `No ${statusLabel(status).toLowerCase()} credit notes`
                : supplierFiltered
                  ? "No credit notes from this supplier"
                  : "No supplier credit notes yet"
            }
            action={status === ALL_STATUSES ? newButton : undefined}
          >
            {status === ALL_STATUSES
              ? "When a supplier refunds or credits you, record it here and apply it to their next bill."
              : "Try another status, or All."}
          </EmptyState>
        ) : (
          <>
            <TableShell>
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Supplier</TableHead>
                    <TableHead className={TH}>Credit note</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Available</TableHead>
                    <TableHead className={cn(TH, "w-[120px] text-right")}>Status</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Created</TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {notes.map((note) => {
                    const name = note.supplierId ? supplierName.get(note.supplierId) : undefined;
                    const available = Number(note.available ?? 0);
                    return (
                      <TableRow
                        key={note.id}
                        {...rowLink(onCreditNoteClick && note.id ? () => onCreditNoteClick(note.id!) : undefined)}
                      >
                        <TableCell className="max-w-[260px]">
                          <div className="flex min-w-0 items-center gap-3">
                            {note.supplierId && onSupplierClick ? (
                              <button
                                type="button"
                                className="truncate text-left font-semibold hover:text-accent-blue hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSupplierClick(note.supplierId!);
                                }}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                {name ?? "View supplier"}
                              </button>
                            ) : (
                              <span className="truncate font-semibold">{name ?? "-"}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[280px]">
                          <div className="font-mono text-[13px] font-semibold">{note.documentNumber || "-"}</div>
                          {note.reason ? (
                            <div className="truncate text-xs text-muted-foreground">{note.reason}</div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          {formatCurrency(note.amount, note.currency)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right text-sm font-semibold tabular-nums",
                            available > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground",
                          )}
                        >
                          {formatCurrency(note.available, note.currency)}
                        </TableCell>
                        <TableCell className="text-right">
                          <StatusPill status={note.status ?? undefined} />
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground tabular-nums">
                          {formatDate(note.createdAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableShell>

            <Pager
              page={currentPage}
              hasPrevious={currentPage > 1}
              hasNext={!!nextCursor}
              onPrevious={handlePrevPage}
              onNext={handleNextPage}
            />
          </>
        )}
      </div>

      <NewSupplierCreditNoteDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        fixedSupplierId={supplierId !== ALL_SUPPLIERS ? supplierId : undefined}
        onCreated={refresh}
      />
    </PageContainer>
  );
}
